import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getGuestStatusFilterOption } from 'utils/helpers';
import { isEventStartingInOneHour } from 'utils/eventUtils';
import { Badge } from '@epoch-ui/components/Badge';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import { Label } from '@epoch-ui/components/Form';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from '@epoch-ui/components/Accordion';
import { Button } from '@epoch-ui/components/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter
} from '@epoch-ui/components/Sheet/sheet';
import { Separator } from '@epoch-ui/components/Separator';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { SearchInput } from '@epoch-ui/components/SearchInput';
import { Icon } from '@epoch-ui/components/Icon';
import { eventGuestsApi } from '@event-editor/api/event-guests';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

type GuestListFilters = {
  statuses?: string[];
  attendance?: string[];
  departments?: string[];
};

interface GuestListFiltersSheetProps extends React.ComponentProps<typeof Sheet> {
  onApply: (filters: GuestListFilters) => void;
  initialFilters?: GuestListFilters;
}

const GuestListFiltersSheet: React.FC<GuestListFiltersSheetProps> = ({
  onApply,
  initialFilters,
  open,
  onOpenChange,
  ...props
}) => {
  const event = useEditableEvent();

  const [departmentSearchText, setDepartmentSearchText] = useState<string | undefined>();

  const [selectedFilters, setSelectedFilters] = useState<GuestListFilters>({
    statuses: initialFilters?.statuses ?? [],
    attendance: initialFilters?.attendance ?? [],
    departments: initialFilters?.departments ?? []
  });

  const { data: guestDepartments, isLoading: isLoadingGuestDepartments } = useQuery({
    queryKey: eventEditorQueryKeys.eventGuestDepartments(event.id!),
    queryFn: () =>
      eventGuestsApi
        .listGuestDepartments(event.id!)
        .then((res) =>
          res.filter((department) =>
            department.toLowerCase().includes(departmentSearchText?.toLowerCase() ?? '')
          )
        ),
    enabled: !!event.id && open
  });

  const rsvpFilterOptions = getGuestStatusFilterOption({
    hasPreEventQuestionsQuestions: (event?.preEventQuestions?.length ?? 0) > 0,
    hasUnlimitedSpots: false, // Not used
    guestHasNoneStatus: event?.guestHasNoneStatus
  });

  const shouldShowAttendanceFilter =
    isEventStartingInOneHour(event) && !event.isUsingEstimatedAttendees;

  const filteredDepartments = useMemo(() => {
    return guestDepartments?.filter((department) =>
      department.toLowerCase().includes(departmentSearchText?.toLowerCase() ?? '')
    );
  }, [guestDepartments, departmentSearchText]);

  const totalSelectedFilters = useMemo(() => {
    return Object.values(selectedFilters).flat().length;
  }, [selectedFilters]);

  const handleFilterChange = useCallback(
    (filter: keyof GuestListFilters, value: string, checked: boolean) => {
      setSelectedFilters((prev) => ({
        ...prev,
        [filter]: checked
          ? [...(prev[filter] ?? []), value]
          : (prev[filter] ?? []).filter((v) => v !== value)
      }));
    },
    []
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        // Reset internal state from initial filters when sheet is closed
        setSelectedFilters({
          statuses: initialFilters?.statuses ?? [],
          attendance: initialFilters?.attendance ?? [],
          departments: initialFilters?.departments ?? []
        });
      }
      onOpenChange?.(nextOpen);
    },
    [onOpenChange, initialFilters]
  );

  return (
    <Sheet open={open} onOpenChange={handleOpenChange} {...props}>
      <SheetContent className="tw-m-2 tw-flex tw-h-[calc(100vh-16px)] tw-w-[480px] tw-flex-col tw-gap-0 tw-rounded-3xl tw-p-0 sm:tw-max-w-[480px]">
        <SheetHeader className="tw-p-6">
          <SheetTitle className="tw-mb-0">
            Filters {totalSelectedFilters > 0 ? `(${totalSelectedFilters})` : ''}
          </SheetTitle>
        </SheetHeader>

        {/* Sheet body */}
        <div className="tw-flex tw-flex-grow tw-flex-col  tw-overflow-auto tw-px-6">
          {/* Selected filter tags */}
          {totalSelectedFilters > 0 && (
            <>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {Object.entries(selectedFilters).map(([filter, value]) =>
                  value.map((v) => (
                    <Badge key={v} variant="primary">
                      {v}
                      <Icon
                        name="x"
                        size={16}
                        className="tw-cursor-pointer"
                        onClick={() =>
                          handleFilterChange(filter as keyof GuestListFilters, v, false)
                        }
                      />
                    </Badge>
                  ))
                )}
              </div>

              <Separator className="tw-mt-4" />
            </>
          )}

          {/* Filter sections */}
          <Accordion type="multiple" defaultValue={['rsvp', 'attendance', 'department']}>
            <AccordionItem value="rsvp">
              <AccordionTrigger>
                RSVP{' '}
                {(selectedFilters.statuses?.length ?? 0) > 0
                  ? `(${selectedFilters.statuses?.length})`
                  : ''}
              </AccordionTrigger>
              <AccordionContent className="tw-flex tw-flex-col tw-gap-2">
                {rsvpFilterOptions.map((option) => (
                  <div key={option} className="tw-flex tw-items-center tw-gap-3">
                    <Checkbox
                      id={option}
                      checked={selectedFilters.statuses?.includes(option)}
                      onCheckedChange={(checked) =>
                        handleFilterChange('statuses', option, checked === true)
                      }
                    />
                    <Label
                      htmlFor={option}
                      className="tw-mb-0 tw-text-sm tw-font-normal tw-leading-5"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>

            {shouldShowAttendanceFilter && (
              <AccordionItem value="attendance">
                <AccordionTrigger>
                  Attendance{' '}
                  {(selectedFilters.attendance?.length ?? 0) > 0
                    ? `(${selectedFilters.attendance?.length})`
                    : ''}
                </AccordionTrigger>
                <AccordionContent className="tw-flex tw-flex-col tw-gap-2">
                  {['Attended', 'Not attended'].map((option) => (
                    <div key={option} className="tw-flex tw-items-center tw-gap-3">
                      <Checkbox
                        id={option}
                        checked={selectedFilters.attendance?.includes(option)}
                        onCheckedChange={(checked) =>
                          handleFilterChange('attendance', option, checked === true)
                        }
                      />
                      <Label
                        htmlFor={option}
                        className="tw-mb-0 tw-text-sm tw-font-normal tw-leading-5"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            )}

            <AccordionItem value="department" className="tw-border-0">
              <AccordionTrigger>
                Department{' '}
                {(selectedFilters.departments?.length ?? 0) > 0
                  ? `(${selectedFilters.departments?.length})`
                  : ''}
              </AccordionTrigger>
              <AccordionContent>
                {isLoadingGuestDepartments ? (
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <Skeleton className="tw-h-4 tw-w-4" />
                    <Skeleton className="tw-h-4 tw-w-24" />
                  </div>
                ) : (
                  <div className="tw-flex tw-flex-col">
                    <SearchInput
                      value={departmentSearchText}
                      className="tw-mb-4 tw-w-full"
                      onChange={(e) =>
                        setDepartmentSearchText(
                          e.target.value.trim().length > 0 ? e.target.value.trim() : undefined
                        )
                      }
                    />
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      {filteredDepartments?.map((department) => (
                        <div key={department} className="tw-flex tw-items-center tw-gap-3">
                          <Checkbox
                            id={department}
                            checked={selectedFilters.departments?.includes(department)}
                            onCheckedChange={(checked) =>
                              handleFilterChange('departments', department, checked === true)
                            }
                          />
                          <Label
                            htmlFor={department}
                            className="tw-mb-0 tw-text-sm tw-font-normal tw-leading-5"
                          >
                            {department}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <hr className="tw-my-0 tw-w-full tw-border-gray-300" />
        <SheetFooter className="tw-flex tw-w-full tw-flex-shrink-0  tw-p-4">
          <Button
            variant="primary"
            size="sm"
            className="tw-rounded-xl"
            onClick={() => onApply(selectedFilters)}
          >
            Apply
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="tw-rounded-xl"
            onClick={() => {
              setSelectedFilters({
                statuses: [],
                attendance: [],
                departments: []
              });
              onApply({
                statuses: [],
                attendance: [],
                departments: []
              });
            }}
          >
            Reset
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export { GuestListFiltersSheet, type GuestListFilters };
